import { z } from 'zod'
import { oc } from '@orpc/contract'
import { billingCaseDtoSchema } from './update-case.contract'
// Relative, never '@/': this file crosses into the web bundle via core-contract, where '@/' is web's own alias.
import { patientInvoiceKindSchema } from '../../copay/handlers/copay-status.contract'

// One endpoint feeds the "Claim & Payment Details" drawer: case + payments + event
// history + hydrated patient/claim/insurance context (accounting-plan Task 11).
// Ledger codes (paymentType/method/status/action) stay plain strings — their value
// sets live in billing-v2-shared.ts, which must not cross the web package boundary.
// Dates cross the boundary as ISO strings; paymentDate/depositDate are calendar
// dates (YYYY-MM-DD, pg `date` columns).

export const caseDetailPaymentSchema = z.object({
  id: z.number(),
  paymentType: z.string(),
  method: z.string(),
  status: z.string(),
  amountCents: z.number(),
  /** EOB figures carried by this payment (Task 26); 0 on copay rows. */
  allowedCents: z.number(),
  patientRespCents: z.number(),
  payerName: z.string().optional(),
  paymentDate: z.string().optional(),
  depositDate: z.string().optional(),
  stripePaymentIntentId: z.string().optional(),
  externalRef: z.string().optional(),
  checkNumber: z.string().optional(),
  accountLast4: z.string().optional(),
  eobDocumentId: z.string().optional(),
  processedAt: z.string().optional(),
  recordedBy: z.string().optional(),
  failureReason: z.string().optional(),
})

export const caseEventSchema = z.object({
  actor: z.string().optional(),
  action: z.string(),
  oldValue: z.unknown().optional(),
  newValue: z.unknown().optional(),
  createdAt: z.string(),
})

export const caseReconciliationSchema = z.object({
  providerChargeCents: z.number().nullable(),
  allowedCents: z.number().nullable(),
  patientRespCents: z.number().nullable(),
  adjustmentCents: z.number().nullable(),
  expectedInsuranceCents: z.number().nullable(),
  /** Σ processed/recorded insurance payments. */
  insurancePaidCents: z.number(),
  /** Σ processed patient payments of every kind. */
  patientPaidCents: z.number().nullable(),
  /** patientResp − patientPaid; null until the EOB is entered. */
  patientBalanceCents: z.number().nullable(),
  /** expectedInsurance − insurancePaid (payer fees); null until the EOB is entered. */
  payerFeesCents: z.number().nullable(),
})

/** One patient Invoice of the encounter (co-pay / deductible / co-insurance) — the drawer's "Patient responsibility" rows and the Record payment prefill. */
export const patientInvoiceSchema = z.object({
  kind: patientInvoiceKindSchema,
  amountCents: z.number().int(),
  status: z.enum(['draft', 'issued', 'balanced', 'cancelled']),
  /** Paid outside Stripe (recorded by hand). */
  manual: z.boolean(),
  /** Invoice-derived UI status — no synchronous Stripe call. */
  stripeStatus: z.enum(['draft', 'awaiting', 'expired', 'paid', 'refunded']),
  collectedOnDos: z.boolean(),
})

export const billingV2CaseDetail = oc
  .route({ method: 'GET', path: '/v2/billing/case/{encounterId}' })
  .input(z.object({ encounterId: z.string() }))
  .output(
    z.object({
      case: billingCaseDtoSchema,
      payments: z.array(caseDetailPaymentSchema),
      /** Newest-first. */
      events: z.array(caseEventSchema),
      patient: z.object({ id: z.string(), name: z.string(), dob: z.string().optional() }),
      /** Primary Coverage of the appointment. */
      insurance: z
        .object({
          payerName: z.string().optional(),
          planName: z.string().optional(),
          memberId: z.string().optional(),
        })
        .optional(),
      /** An uninsured visit paying the flat self-pay fee: the drawer shows the self-pay row, not the EOB kinds. */
      selfPay: z.boolean(),
      /** One per existing patient Invoice, in kind order. */
      patientInvoices: z.array(patientInvoiceSchema),
      reconciliation: caseReconciliationSchema,
    }),
  )
